import { PREFILL_FIELD_RULES } from '@shared';

import type { PrefillDraft } from '../store/store';

export type PrefillValidationErrors = Partial<Record<keyof PrefillDraft, string>>;

export function parseCurrencyInput(value: string): number {
  return Number(value.trim().replace(/[$,\s]/g, ''));
}

function validateNumber(
  value: string,
  rules: { min: number; max: number; integer?: boolean },
  label: string,
): string | undefined {
  const parsed = parseCurrencyInput(value);

  if (!Number.isFinite(parsed)) return `${label} must be a number`;
  if ('integer' in rules && rules.integer && !Number.isInteger(parsed)) {
    return `${label} must be a whole number`;
  }
  if (parsed < rules.min || parsed > rules.max) {
    return `${label} must be between ${rules.min.toLocaleString()} and ${rules.max.toLocaleString()}`;
  }
  return undefined;
}

export function validatePrefillDraft(draft: PrefillDraft): PrefillValidationErrors {
  const errors: PrefillValidationErrors = {};

  if (draft.zipCode.trim() && !/^\d{5}$/.test(draft.zipCode.trim())) {
    errors.zipCode = 'Enter a 5-digit ZIP code';
  }

  if (draft.birthDate) {
    const year = draft.birthDate.getFullYear();
    const today = new Date();
    today.setHours(23, 59, 59, 999);

    if (Number.isNaN(draft.birthDate.getTime())) {
      errors.birthDate = 'Enter a valid date';
    } else if (year < PREFILL_FIELD_RULES.clientBirthDate.minYear) {
      errors.birthDate = `Year must be ${PREFILL_FIELD_RULES.clientBirthDate.minYear} or later`;
    } else if (draft.birthDate > today) {
      errors.birthDate = 'Birth date cannot be in the future';
    }
  }

  const nameFields = [
    ['partnerFirstName', PREFILL_FIELD_RULES.partnerFirstName.maxLength, "Partner's first name"],
    ['partnerLastName', PREFILL_FIELD_RULES.partnerLastName.maxLength, "Partner's last name"],
  ] as const;

  for (const [field, maxLength, label] of nameFields) {
    if (draft[field].trim().length > maxLength) {
      errors[field] = `${label} must be ${maxLength} characters or fewer`;
    }
  }

  const numberFields = [
    ['householdSize', PREFILL_FIELD_RULES.householdHouseholdSize, 'Household size'],
    ['totalAssets', PREFILL_FIELD_RULES.householdTotalAssetsValue, 'Net worth'],
    ['priorYearIncome', PREFILL_FIELD_RULES.householdPriorYearIncome, 'Household income'],
  ] as const;

  for (const [field, rules, label] of numberFields) {
    if (!draft[field].trim()) continue;
    const error = validateNumber(draft[field], rules, label);
    if (error) errors[field] = error;
  }

  return errors;
}
